import { OrgBaseInterface } from "../Shared/OrgBase.Interace";
import { BotSequenceType } from "./BotSequence.Enums";

// LINK libs/entities/src/BotSequences/BotSequenceScheduledAttempt.Entity.ts

/**
 * The state machine of one Scheduled Attempt (scheduler-implementation-design.md +
 * the ratified scheduling flow). One row per scheduling decision — booked OR skipped.
 * The scheduler creates rows (`SCHEDULED` / `SKIPPED_*` / `SIMULATED`); the dispatcher
 * moves `SCHEDULED → DISPATCHED`; the handler moves `DISPATCHED → SENDING → FIRED/FAILED`,
 * `DISPATCHED → CANCELLED` (recheck failed), or `SENDING → CANCELLED` (the bot reported a
 * definitive no-send: opt-out or a live escalated conversation — outcome known, nothing sent).
 * String values match the flow doc's `skipped:*` wire names.
 */
export enum BotSequenceScheduledAttemptStatus {
  /** Booked by the scheduler, awaiting fire time. The only status the dispatcher claims. */
  SCHEDULED = "scheduled",
  /** Claimed by the dispatcher, handed to the handler. Written BEFORE the handoff. Stuck here = nothing sent, safe to re-dispatch. */
  DISPATCHED = "dispatched",
  /** Handler recheck passed; written immediately BEFORE the channel send. Stuck here = outcome unknown, never auto-resend. */
  SENDING = "sending",
  /** Handler recheck passed and EVERY channel of the step went out. */
  FIRED = "fired",
  /**
   * Some channels went out and some failed or were skipped (ENG-3216 decision 38). Terminal;
   * the cursor advances (something reached the tenant); each failed channel keeps its reason
   * in `metadata.deliveries`. Never reads as `fired`.
   */
  INCOMPLETE = "incomplete",
  /** Handler recheck failed at fire time (e.g. extension granted mid-day), or every channel was skipped. */
  CANCELLED = "cancelled",
  /** Send was attempted and nothing went out (≥1 channel errored, none sent). */
  FAILED = "failed",
  /** Step due but the day was not fireable (weekend / holiday / blackout). Steps roll forward. */
  SKIPPED_HOLIDAY = "skipped:holiday",
  /** Step due but collapsed into a later step's fire (collapse-to-latest). */
  SKIPPED_SUPERSEDED = "skipped:superseded",
  /** Step due but the enrollment is agent-suppressed (suppressUntil > now). Steps stay due. */
  SKIPPED_SUPPRESSED = "skipped:suppressed",
  /** Latest due step older than the staleness horizon (late-arriving data / backfill). Never fires. */
  SKIPPED_STALE = "skipped:stale",
  /** SIMULATED-mode booking: the full decision, terminal at creation, never dispatched. */
  SIMULATED = "simulated"
}

/** The `skipped:*` subset — decisions that book nothing but are recorded in the ledger. */
export const BOT_SEQUENCE_SKIPPED_STATUSES: readonly BotSequenceScheduledAttemptStatus[] = [
  BotSequenceScheduledAttemptStatus.SKIPPED_HOLIDAY,
  BotSequenceScheduledAttemptStatus.SKIPPED_SUPERSEDED,
  BotSequenceScheduledAttemptStatus.SKIPPED_SUPPRESSED,
  BotSequenceScheduledAttemptStatus.SKIPPED_STALE
];

/** The statuses that occupy the "one live booking per enrollment per local day" unique slot. */
export const BOT_SEQUENCE_LIVE_BOOKING_STATUSES: readonly BotSequenceScheduledAttemptStatus[] = [
  BotSequenceScheduledAttemptStatus.SCHEDULED,
  BotSequenceScheduledAttemptStatus.DISPATCHED,
  BotSequenceScheduledAttemptStatus.SENDING,
  BotSequenceScheduledAttemptStatus.FIRED,
  BotSequenceScheduledAttemptStatus.INCOMPLETE
];

// ── Per-channel delivery ledger (ENG-3216 decision 38) ─────────────────────────────

/** The things one step fire can do; `fmsNote` is a side action and never decides the attempt status. */
export type BotSequenceDeliveryKey = "sms" | "voice" | "email" | "fmsNote";

/**
 * Write-ahead states of one ledger entry: `pending` is seeded at `sending`, `started` is
 * claimed by the process about to act, and the rest are terminal.
 */
/**
 * `simulated` (Outbound Gate, ENG-3373): built in full and recorded, nothing left Swivl — counts like
 * `sent` for the attempt and the cursor. `blocked`: the person opted out (decision 7) — counts like
 * `skipped`, with its own name so it is never mistaken for something we chose not to send.
 */
export type BotSequenceDeliveryStatus = "pending" | "started" | "sent" | "simulated" | "blocked" | "failed" | "skipped";

/** How a sequence call ended (decision 36). */
export type BotSequenceCallOutcome = "answered" | "voicemail_left" | "voicemail_skipped" | "no_answer" | "busy" | "failed";

export interface BotSequenceDeliveryOutcome {
  status: BotSequenceDeliveryStatus;
  /** ISO timestamp of the last state change. */
  at: string;
  /** Machine-readable skip/failure reason: `missing_variable:Lease:Balance`, `kill_switch_off`, `no_email_on_user`, `no-answer`, … */
  reason?: string;
  /**
   * Who this delivery went to (decision 65): the phone number texted or dialed, the email
   * address, or the FMS system and tenant for the note. Written when the delivery starts, so a
   * wrong number or a stale address is visible on the attempt itself.
   */
  recipient?: string;
  conversationId?: number;
  messageId?: number;
  /** Twilio message SID (sms) / call SID (voice). */
  externalSid?: string;
  /** Provider id for email (Mandrill message id) and the FMS note id. */
  externalId?: string;
  sandboxed?: boolean;
  /** Voice: the Twilio call SID (also mirrored in `externalSid`). */
  callSid?: string;
  /** Voice: Twilio `AnsweredBy` as reported by asynchronous machine detection. */
  answeredBy?: string;
  /**
   * Voice: when the call connected (Twilio's answer webhook). The entry stays `started` for
   * the length of the call and turns `sent` with its final outcome when the call ends
   * (decision 61); the sweeper leaves an answered, still-running call alone.
   */
  answeredAt?: string;
  callOutcome?: BotSequenceCallOutcome;
  /** Outbound Gate: why this delivery was simulated (`flag_off`, `not_listed`, …) or blocked (`opted_out`). */
  gateReason?: string;
  /** Outbound Gate: the exact text that would have gone (simulated), so the record is complete without a send. */
  content?: string;
  /** Decision 12: a Swivl system send, not an org's. */
  system?: boolean;
  /** Decision 7: a legally required send that went despite the person's opt-out. */
  consentOverridden?: boolean;
  /**
   * Voice: what decided the call had reached a machine (decision 64). `twilio` = Twilio's verdict;
   * the other two = the greeting's words judged MACHINE, then our own beep detector heard the
   * beep / heard only silence. `answeredBy` still records Twilio's verdict whenever it arrives.
   */
  machineDetectedBy?: "twilio" | "greeting_words_then_beep" | "greeting_words_then_silence";
  callDurationSeconds?: number;
  /** FMS note: variables that rendered as "(unavailable)" (decision 27). */
  missingVars?: string[];
}

/**
 * ENG-3512 (legacy-send-cutover.md): the legacy sequence engine already sent this step for the
 * same episode — the fire recheck's `metadata.cancelReason`. Temporary — deleted with the legacy
 * engine.
 */
export const BOT_SEQUENCE_LEGACY_SENT_REASON = "legacy_sent";

/**
 * The state-transition timestamp trail + decision evidence carried on each attempt.
 * One timestamp key per state the row has entered, written in the SAME statement as the
 * `status` transition it records (the write-ahead checkpoint and its stamp are one write).
 * Keys are never deleted; a re-dispatch refreshes `dispatchedAt` and bumps `redispatchCount`.
 */
export interface BotSequenceScheduledAttemptMetadata {
  scheduledAt?: string;
  dispatchedAt?: string;
  sendingAt?: string;
  firedAt?: string;
  cancelledAt?: string;
  failedAt?: string;
  simulatedAt?: string;
  skippedHolidayAt?: string;
  skippedSupersededAt?: string;
  skippedSuppressedAt?: string;
  skippedStaleAt?: string;
  /**
   * ENG-3216 decision 69: a `skipped:stale` row for a ladder day that was already past when the
   * enrollment was created. Missed days never fire, "Always send on this day" included — turning a
   * sequence on must never release a backlog.
   */
  staleReason?: "before_enrollment";
  /** With `staleReason: before_enrollment`: every ladder day that was already past (the row sits on the latest); one row per enrollment (Chuck, 2026-09-24). */
  missedOffsets?: number[];
  /** Number of times the dispatcher re-claimed this row after a `dispatched` timeout. */
  redispatchCount?: number;
  /** Why a cancel happened outside the handler recheck (e.g. the dispatcher's past-fireDate guard: `past_fire_date`). */
  cancelReason?: string;
  /** Decision evidence: the channel error recorded when the send failed. */
  failureReason?: string;
  /** The bot conversation the fired message opened (handler, on `fired`). */
  conversationId?: number;
  /** The BotMessage id of the generated assistant turn (handler, on `fired`). */
  messageId?: number;
  /** True when the send was sandboxed by the non-prod recipient allowlist — treated as fired. */
  sandboxed?: boolean;
  /** True when a test run finalized this attempt without a bot fire request (full-flow-test-run.md). */
  simulatedSend?: boolean;
  /** The bot_sequence_test_run row that owned this attempt's simulated send. */
  testRunId?: number;
  /** Config snapshot references at booking time (channel, mode, window) for the debug view. */
  configSnapshot?: Record<string, unknown>;
  /** Stamped once by the stale-`sending` sweeper when delivery evidence is ambiguous — the row waits for a human. */
  sweeperParkedAt?: string;
  /** Written with the `incomplete` transition (decision 38). */
  incompleteAt?: string;
  /** The per-channel ledger of this fire (decision 38). Seeded `pending` at `sending`; every process that acts writes its entry. */
  deliveries?: Partial<Record<BotSequenceDeliveryKey, BotSequenceDeliveryOutcome>>;
  /** True when the step was booked under "Always send on this day" (`deferrable: false`). */
  mustSend?: boolean;
  /** Booked while the location's mode was SIMULATED (ENG-3373): a full dry run whose deliveries the gate withholds. */
  simulatedMode?: boolean;
  /** How many deliveries of this fire actually left Swivl (`sent`) — written with the final transition. */
  realDeliveries?: number;
  /** How many deliveries the Outbound Gate withheld (`simulated`) — written with the final transition. */
  simulatedDeliveries?: number;
  /** The Ticket opened for the location when a must-send step could not go out on its date (decision 16). */
  ticketId?: number;
}

export interface BotSequenceScheduledAttemptInterface extends OrgBaseInterface {
  /** Denormalized for the admin debug view; the decision was made for `enrollmentId`. */
  locationId: number | null;
  userId: number;
  enrollmentId: number;
  sequenceType: BotSequenceType;
  /** The ladder offset this attempt represents (the latest due step for booked rows). */
  stepOffset: number;
  /** The location-local day the decision belongs to — the unique-booking key and the day-boundary cancel key. */
  localDay: string;
  /** Fire time; null for `skipped:*` rows. */
  fireAt: Date | null;
  status: BotSequenceScheduledAttemptStatus;
  metadata: BotSequenceScheduledAttemptMetadata;
}

// LINK libs/entities/src/BotSequences/BotSequenceSchedulerRun.Entity.ts

/** Which invocation produced a scheduler run row. */
export enum BotSequenceSchedulerRunType {
  /** The morning cohort run — one row per org per local day (double-run guard). */
  MORNING_RUN = "MORNING_RUN",
  /** A reconciler enrollment-creation handoff — the day-0 / late-arriving-data path. Unlimited per day. */
  BOOK_REQUEST = "BOOK_REQUEST"
}

export enum BotSequenceSchedulerRunStatus {
  /** Claimed/receipt written; the background job has not started it yet. */
  PENDING = "PENDING",
  RUNNING = "RUNNING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED"
}

/** The run's one-line audit: booked + per-reason skip totals + day-boundary cancels. */
export interface BotSequenceSchedulerRunCounters {
  booked: number;
  simulated: number;
  skippedHoliday: number;
  skippedSuperseded: number;
  skippedSuppressed: number;
  skippedStale: number;
  /** Attempts cancelled because their local day ended (morning-run day-boundary sweep). */
  cancelledStale: number;
  /** Enrollments evaluated that had no due step / config off — accounted for, produced no row. */
  noDueStep: number;
}

export interface BotSequenceSchedulerRunInterface extends OrgBaseInterface {
  type: BotSequenceSchedulerRunType;
  /** The org's local run day (westernmost resolved timezone) — the MORNING_RUN double-run key. */
  localRunDate: string;
  status: BotSequenceSchedulerRunStatus;
  startedAt: Date | null;
  completedAt: Date | null;
  /** Requested enrollment ids — filled for BOOK_REQUEST, empty for MORNING_RUN (its scope is the org cohort). */
  enrollmentIds: number[];
  counters: BotSequenceSchedulerRunCounters | null;
  error: string | null;
}
